import { Injectable } from '@angular/core';
import { forkJoin, observable, Observable } from 'rxjs';
import {
  Context,
  MedicalCentersRespsonse,
  MedicalDoctorsResponse,
  MedicalGuide,
  PruebasDiagnosticas,
  SearchEngineItem,
  SearchEngineResponse,
  Speciality,
  UrlServicePath,
} from '../models/api';
import { MockPath } from '../models/http-helper';
import {
  AppState,
  Province,
  SearchFilter,
  SearchParams,
} from '../models/state';
import { FileService } from './file.service';
import { HttpHelperService } from './http-helper.service';
import { UiService } from './ui.service';
import { UrlService } from './url.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private httpHelper: HttpHelperService,
    private urlService: UrlService,
    private ui: UiService,
    private fileHelper: FileService,
  ) {}

  getMedicalCenters(
    state: Partial<AppState>,
    urlPath?: UrlServicePath,
    pageSize = 10,
    radio = 10
  ): Observable<Partial<MedicalCentersRespsonse>> {
    const searchParams = state?.searchParams;
    const searchFilters = state?.searchFilters;
    const codGuia = this.getMedicalGuideId(state);
    const baseUrl = this.urlService.getApiBaseUrl();
    let url = `${baseUrl}salud/cuadroMedico/guiaMedica/${codGuia}/centrosMedicos`;
    if (urlPath) url += '/' + urlPath;
    const latAndLngQueryParams = `&latitud=${searchParams?.lat}&longitud=${searchParams?.lng}`;
    const latAndLngIndicatorsQueryParams = `&indUrgencias=${searchFilters?.urgency ? true : false}&indMapfreSalud=${searchFilters?.mapfreSalud ? true : false}&indCitaOnline=${searchFilters?.onlineBooking ? true : false}`;
    const paginationQueryParams = `&pageNumber=${state.centersPage?.toString()}&pageSize=${pageSize.toString()}&radio=${state?.radio?.toString() || radio.toString() || ''}`;
    const mainQueryParams = this.getMainQueryParams({ urlPath, searchParams, searchFilters });
    url = `${url}${mainQueryParams}${latAndLngQueryParams}${latAndLngIndicatorsQueryParams}${paginationQueryParams}`;
    return this.httpHelper.get(url, { mockPath: 'medical-centers' });
  }

  getAditionalServices(
    state: Partial<AppState>,
    pageSize = 10,
    radio = 10
  ): Observable<Partial<MedicalCentersRespsonse>> {
    const searchParams = state?.searchParams;
    const baseUrl = this.urlService.getApiBaseUrl();

    const searchFilters = state?.searchFilters;
    const nombreCentroMedico = searchFilters?.centerName || '';
    const codigoSubespecialidad = searchParams?.subSpeciality?.codigoSubespecialidad || '';
    const nombreFacultativo = searchFilters?.doctorName || '';

    let url = `${baseUrl}salud/cuadroMedico/serviciosAdicionales/centrosMedicos?`;
    const mainQueryParams = `codigoSubespecialidad=${codigoSubespecialidad}&nombreCentroMedico=${nombreCentroMedico}&nombreFacultativo=${nombreFacultativo}`;
    const latAndLngQueryParams = `&latitud=${searchParams?.lat}&longitud=${searchParams?.lng}`;
    const paginationQueryParams = `&pageNumber=${state.centersPage.toString()}&pageSize=${pageSize.toString()}&radio=${state?.radio?.toString() || radio.toString() || ''}`;
    const latAndLngIndicatorsQueryParams = `&indMapfreSalud=${searchFilters?.mapfreSalud ? true : false}`;

    url = `${url}${mainQueryParams}${latAndLngQueryParams}${latAndLngIndicatorsQueryParams}${paginationQueryParams}`;
    return this.httpHelper.get(url, { mockPath: 'additional-services-list' });
  }

  getMedicalDoctors(
    state: Partial<AppState>,
    pageSize = 10,
    radio = 10
  ): Observable<Partial<MedicalDoctorsResponse>> {
    const searchParams = state?.searchParams;
    const searchFilters = state?.searchFilters;
    const subSpeciality = searchParams?.subSpeciality;
    const codGuia = this.getMedicalGuideId(state);
    const nombreCentroMedico = searchFilters?.centerName || '';
    const baseUrl = this.urlService.getApiBaseUrl();
    const speciality = searchParams?.speciality;
    const codigoEspecialidad = speciality?.codigoEspecialidad || '';
    const codigoSubespecialidad = subSpeciality?.codigoSubespecialidad || '';
    const nombreFacultativo = searchFilters?.doctorName || '';

    let url = `${baseUrl}salud/cuadroMedico/guiaMedica/${codGuia}/facultativos`;
    const mainQueryParams = `?codigoEspecialidad=${codigoEspecialidad}&codigoSubespecialidad=${codigoSubespecialidad}&nombreCentroMedico=${nombreCentroMedico}&nombreFacultativo=${nombreFacultativo}`;
    const latAndLngQueryParams = `&latitud=${searchParams?.lat}&longitud=${searchParams?.lng}`;
    const latAndLngIndicatorsQueryParams = `&indUrgencias=${
      searchFilters?.urgency ? true : false
    }&indMapfreSalud=${
      searchFilters?.mapfreSalud ? true : false
    }&indCitaOnline=${searchFilters?.onlineBooking ? true : false}`;
    const paginationQueryParams = `&pageNumber=${state.doctorsPage?.toString()}&pageSize=${pageSize.toString()}&radio=${
      state?.radio?.toString() || radio.toString() || ''
    }`;

    url = `${url}${mainQueryParams}${latAndLngQueryParams}${latAndLngIndicatorsQueryParams}${paginationQueryParams}`;
    return this.httpHelper.get(url, { mockPath: 'doctors' });
  }

  getDiagnosticTests(
    state: Partial<AppState>,
    centerCode = null
  ): Observable<PruebasDiagnosticas[]> {
    const codGuia = this.getMedicalGuideId(state);
    const searchFilters = state?.searchFilters;
    const baseUrl = this.urlService.getApiBaseUrl();
    const codigoCentro = centerCode || '';
    const indCitaOnline = searchFilters?.onlineBooking ? 'true' : '';
    const url = `${baseUrl}salud/cuadroMedico/guiaMedica/${codGuia}/pruebasDiagnosticas?codigoCentro=${codigoCentro}&indCitaOnline=${indCitaOnline}`;
    return this.httpHelper.get(url, { mockPath: 'pruebas-diagnosticas' });
  }

  getSpecialities(
    state: Partial<AppState>,
    centerCode = null
  ): Observable<Speciality[]> {
    const codGuia = this.getMedicalGuideId(state);
    const searchFilters = state?.searchFilters;
    const baseUrl = this.urlService.getApiBaseUrl();
    const codigoCentro = centerCode || '';
    const indCitaOnline = searchFilters?.onlineBooking ? 'true' : '';
    const url = `${baseUrl}salud/cuadroMedico/guiaMedica/${codGuia}/especialidades?codigoCentro=${codigoCentro}&indCitaOnline=${indCitaOnline}`;
    return this.httpHelper.get(url, { mockPath: 'specialities' });
  }

  getSpecialitiesByCode(
    specialityCode: string
  ): Observable<Speciality[]> {
    const baseUrl = this.urlService.getApiBaseUrl();
    const url = `${baseUrl}salud/cuadroMedico/especialidad/${specialityCode}`;
    return this.httpHelper.get(url, { mockPath: 'specialities-id' });
  }

  getSearchEngine(
    searchList: string[],
    state: Partial<AppState>
  ): Observable<SearchEngineItem[]> {
    const observers = [];
    for (const ele of searchList) {
      const baseUrl = this.urlService.getApiBaseUrl();
      const url = `${baseUrl}configuracion/buscador/${ele}`;
      observers.push(this.httpHelper.get(url, { mockPath: ele as MockPath }));
    }
    return forkJoin(observers);
  }

  async getSearchEngineList(): Promise<SearchEngineResponse> {
    const baseUrl = this.urlService.getApiBaseUrl();
    const url = `${baseUrl}configuracion/salud/cuadroMedico/buscadores`;
    return this.httpHelper
      .get(url, { mockPath: 'search-engine-list' })
      .toPromise();
  }

  getMedicalGuides(): Observable<MedicalGuide[]> {
    const baseUrl = this.urlService.getApiBaseUrl();
    const url = `${baseUrl}salud/cuadroMedico/guiasMedicas`;
    return this.httpHelper.get(url, { mockPath: 'medical-guides' });
  }

  getProvinces() {
    const baseUrl = this.urlService.getApiBaseUrl();
    const url = `${baseUrl}salud/cuadroMedico/provincias`;
    return this.httpHelper.get(this.getBeApiUrl(url), { mockPath: 'provinces' });
  }

  getBeApiUrl(endpoint: string): string {
    const baseHref = document
      .getElementsByTagName('base')[0]
      .href.replace(Context.FE, Context.BE);
    return `${baseHref}${endpoint}`;
  }

  downloadMedicalGuidePdf(medicalGuide: MedicalGuide, province: Province) {
    const baseUrl = this.urlService.getApiBaseUrl();
    const url = `${baseUrl}salud/cuadroMedico/guiaMedica/${medicalGuide?.codigoGuiaMedica}/provincia/${province.codigoProvincia}/documento`;
    return this.httpHelper.get(url).subscribe(
      (base64: string) => {
        this.fileHelper
          .getDocumentFromBase64(
            base64,
            'application/pdf',
            `${medicalGuide.descripcionProductoGenerico
              .replace(/\s+/g, '_')
              ?.toLowerCase()}.pdf`
          )
          .then((doc) => {
            this.fileHelper.open(doc);
          });
      },
      () => {
        this.ui.closeAllDialogs();
        this.ui.openServerErrorModal();
      }
    );
  }

  downloadAdditionalServicesPdf() {
    const baseUrl = this.urlService.getApiBaseUrl();
    const url = `${baseUrl}salud/cuadroMedico/serviciosAdicionales/documento`;
    return this.httpHelper.get(url).subscribe(
      (base64: string) => {
        this.fileHelper
          .getDocumentFromBase64(
            base64,
            'application/pdf',
            `servicios-adicionales.pdf`
          )
          .then((doc) => {
            this.fileHelper.open(doc);
          });
      },
      () => {
        this.ui.closeAllDialogs();
        this.ui.openServerErrorModal();
      }
    );
  }

  private getMedicalGuideId(state: Partial<AppState>): string {
    return (
      state.codGuia ||
      state?.searchParams?.medicalGuide?.codigoGuiaMedica ||
      '08'
    );
  }

  private getMainQueryParams({
    urlPath,
    searchParams,
    searchFilters,
  }: {
    urlPath: UrlServicePath;
    searchParams: Partial<SearchParams>;
    searchFilters: Partial<SearchFilter>;
  }): string {
    const nombreCentroMedico = searchFilters?.centerName || '';
    const nombreFacultativo = searchFilters?.doctorName || '';

    if (urlPath === 'pruebasDiagnosticas') {
      const diagnosticTest = searchParams?.diagnosticTest;
      const bodyPart = searchParams?.bodyPart;
      const codigoActo = diagnosticTest?.codigo || '';
      const codigoRegionAnatomica = bodyPart?.codigo || '';
      return `?codigoActo=${codigoActo}&codigoRegionAnatomica=${codigoRegionAnatomica}&nombreCentroMedico=${nombreCentroMedico}&nombreFacultativo=${nombreFacultativo}`;
    }

    const speciality = searchParams?.speciality;
    const subSpeciality = searchParams?.subSpeciality;
    const codigoEspecialidad = speciality?.codigoEspecialidad || '';
    const codigoSubespecialidad = subSpeciality?.codigoSubespecialidad || '';
    return `?codigoEspecialidad=${codigoEspecialidad}&codigoSubespecialidad=${codigoSubespecialidad}&nombreCentroMedico=${nombreCentroMedico}&nombreFacultativo=${nombreFacultativo}`;
  }
}
